import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import PaymentSuccessHeader from '../../components/PaymentSuccessHeader/PaymentSuccessHeader';
import PaymentFailedHeader from '../../components/PaymentFailedHeader/PaymentFailedHeader';
import InvoiceItemsList from '../../components/InvoiceItemsList/InvoiceItemsList';
import formatMoney from '../../utils/formatMoney';
import isSupportedCard from '../../utils/isSupportedCard';
import './styles.css';

const PaymentStatus = () => {
  const location = useLocation();
  const invoiceTitle = get(location, 'state.invoiceTitle', '');
  const invoiceNumber = get(location, 'state.invoiceNumber', '');
  const remainingBalance = get(location, 'state.remainingBalance', '0');
  const amountDue = get(location, 'state.amountDue', '0');
  const surchargeFee = get(location, 'state.surchargeFee');
  const dueDate = get(location, 'state.dueDate', '');
  const transactionInfo = get(location, 'state.transactionInfo', '');
  const contactName = startCase(get(location, 'state.contactName', ''));
  const paymentSucceeded = get(location, 'state.success', true);
  const errors = get(location, 'state.errors', null);
  const invoiceItems = get(location, 'state.invoiceItems', []);
  const lastFour = get(location, 'state.lastFour', '');
  const cardType = get(location, 'state.cardType');
  const pastPayments = get(location, 'state.pastPayments', []);
  const createdDate = get(location, 'state.createdDate', new Date());
  // eslint-disable-next-line no-restricted-globals
  const isBalanceDue = parseFloat(remainingBalance) !== 0 && !(isNaN(remainingBalance));
  const [isPrinting, setIsPrinting] = useState(false);

  return (
    <Card className="PaymentStatus">
      <CardContent id="payment-status">
        <div>
          {paymentSucceeded && (
            <PaymentSuccessHeader
              invoiceTitle={invoiceTitle}
              invoiceNumber={invoiceNumber}
              transactionAmount={get(transactionInfo, 'transaction_amount', null)}
              transactionDate={get(transactionInfo, 'transaction_date', null)}
              isPrinting={isPrinting}
            />
          )}
          {!paymentSucceeded && (
            <PaymentFailedHeader errors={errors} />
          )}
          <Divider />
          <div>
            <div className="payment-status-payment-method-label">
              Payment Method
            </div>
            <div className={paymentSucceeded ? 'payment-status-payment-method-text' : 'payment-status-payment-method-text-failed'}>
              <div className="transaction-card-holder-name">{contactName}</div>
              <div className="transaction-card-type-wrapper">
                <div className="payment-status-last-four">
                  {`**** ${get(transactionInfo, 'last_four', lastFour)}`}
                </div>
                {isSupportedCard(cardType) && cardType ? <img className="cc-img" src={`${process.env.PUBLIC_URL}/img/${cardType}.png`} alt="logo" /> : null}
                {!isSupportedCard(cardType) || !cardType ? <CreditCardIcon className="credit-card-icon" /> : null}
              </div>
            </div>
            <Divider />
            <div className="payment-status-transaction-id">
              <div>Transaction ID</div>
              <div>{get(transactionInfo, 'transaction_id', null)}</div>
            </div>
            <div className="payment-status-auth-code">
              <div>Authorization Code</div>
              <div>{get(transactionInfo, 'auth_code', null)}</div>
            </div>
          </div>
          <Divider />
          {!paymentSucceeded && (
            <InvoiceItemsList
              amountDue={amountDue}
              surchargeFee={surchargeFee}
              remainingBalance={remainingBalance}
              invoiceItems={invoiceItems}
              createdDate={createdDate}
              pastPayments={pastPayments}
            />
          )}
          <div className="payment-status-remaining-balance-paid">
            <div>
              <div>
                <div className={isBalanceDue ? 'balance-due-text' : 'balance-paid-text'}>Remaining Balance</div>
                <br />
                {isBalanceDue && (
                  <div style={{ paddingLeft: '16px' }}>
                    {`Due by ${format(new Date(dueDate), 'MMMM dd, yyyy')}`}
                  </div>
                )}
                <br />
              </div>
            </div>
            <div>
              <div>
                <div className={isBalanceDue ? 'balance-due-text' : 'balance-paid-text'}>{formatMoney(remainingBalance)}</div>
                <div />
              </div>
            </div>
          </div>
        </div>
        <div className="payment-status-print-button">
          {!isPrinting && (
            <Button
              type="submit"
              variant="contained"
              disabled={!paymentSucceeded}
              onClick={() => {
                setIsPrinting(true);
                // eslint-disable-next-line new-cap
                const doc = new jsPDF('p', 'pt', 'a4', true);
                const source = document.getElementById('payment-status') || '';
                doc.html(source, {
                  callback(docOutput) {
                    setIsPrinting(false);
                    const confirmationPDF = docOutput.output('blob');
                    const confirmationPDFURL = URL.createObjectURL(confirmationPDF);
                    window.open(confirmationPDFURL, '_blank');
                  },
                  x: 0,
                  y: 0,
                });
              }}
              style={{
                fontWeight: 'bold',
                color: '#fff',
                height: '60px',
                width: '20%',
                backgroundColor: paymentSucceeded ? '#00A9E0' : 'gray',
              }}
            >
              Print
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentStatus;
